import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, BackendIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const CAPABILITIES = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
    title: "REST & GraphQL APIs",
    desc: "Type-safe, versioned APIs with auto-generated docs (OpenAPI), rate limiting, auth middleware, and comprehensive error handling.",
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
      </svg>
    ),
    title: "Microservices",
    desc: "Domain-driven service decomposition, service mesh (Istio / Linkerd), gRPC inter-service communication, and distributed tracing.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
    title: "Real-Time Systems",
    desc: "WebSocket servers, event-driven architectures with Kafka and RabbitMQ, push notifications, and sub-10ms pub/sub pipelines.",
    color: "border-yellow-500/30 bg-yellow-500/5",
    accent: "text-yellow-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 2.25v2.25m-16.5-2.25v2.25m16.5 2.25v2.25m-16.5-2.25v2.25" />
      </svg>
    ),
    title: "Database Architecture",
    desc: "Schema design, query optimisation, read replicas, sharding strategies, and migrations for PostgreSQL, MongoDB, and Redis.",
    color: "border-blue-500/30 bg-blue-500/5",
    accent: "text-blue-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25z" />
      </svg>
    ),
    title: "Auth & Security",
    desc: "OAuth2, JWT, RBAC, SAML SSO, secrets management (Vault), SQL injection prevention, and penetration-test-ready codebases.",
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
      </svg>
    ),
    title: "Scalability & Performance",
    desc: "Horizontal autoscaling, caching layers (Redis, CDN), connection pooling, and load testing to 10× projected peak traffic.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
  },
];

const TECH = [
  "Node.js", "Python", "Go", "PostgreSQL", "MongoDB", "Redis",
  "Kafka", "Docker", "Kubernetes", "GraphQL", "gRPC", "Elasticsearch",
];

const PROCESS = [
  {
    step: "01",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    ),
    title: "System Design",
    desc: "We design the architecture first — data models, service boundaries, API contracts, and scaling strategies before any code.",
  },
  {
    step: "02",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
      </svg>
    ),
    title: "Core Infrastructure",
    desc: "Databases, caching layers, message queues, and CI/CD pipelines set up and hardened from day one.",
  },
  {
    step: "03",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25z" />
      </svg>
    ),
    title: "API Development",
    desc: "Endpoints built TDD-style with integration tests, contract tests, and load tests baked in at every sprint.",
  },
  {
    step: "04",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" />
      </svg>
    ),
    title: "Performance Tuning",
    desc: "Query profiling, N+1 elimination, index optimisation, and load testing before any production traffic.",
  },
  {
    step: "05",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
      </svg>
    ),
    title: "Observability",
    desc: "Structured logging, distributed tracing (OpenTelemetry), metrics dashboards, and on-call alerting from launch.",
  },
];

const FEATURES = [
  "99.9%+ uptime SLA design",
  "Horizontal autoscaling",
  "Zero-downtime deployments",
  "Comprehensive API documentation",
  "Rate limiting & abuse prevention",
  "GDPR-compliant data handling",
  "Multi-region failover",
  "Automated database backups",
  "Full audit logging",
  "Load-tested to 10x peak",
  "gRPC & REST endpoints",
  "Integration test suite",
];

export default function BackendDetail() {
  useSEO({
    title: "Backend & API Developers in Texas | BerryByte",
    description: "Expert backend developers in Texas. BerryByte builds REST APIs, GraphQL, microservices & database architecture — custom API development services built to scale.",
    keywords: "api developer texas, backend developer texas, backend development in texas, database developer texas, custom api development services texas, Node.js development, microservices architecture",
    canonical: "https://www.berrybyteinn.com/services/backend-engineering",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-emerald-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <BackendIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-emerald-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / Backend Engineering
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Node.js · Python · Go · PostgreSQL
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Backend That<br />
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Doesn't Break.</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              Scalable APIs, microservices, and database architectures built for millions of users. We design for 10x your projected peak traffic — before writing line one.
            </p>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-semibold shadow-2xl shadow-emerald-900/40 hover:shadow-emerald-900/60 hover:-translate-y-1 transition-all duration-300">
              Start Your Project<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "50+", label: "APIs Built" }, { value: "99.9%", label: "Avg Uptime" }, { value: "10x", label: "Traffic Tested" }, { value: "<50ms", label: "Avg API Response" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-emerald-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-emerald-400 font-semibold text-sm tracking-widest uppercase">Capabilities</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Everything behind the curtain,<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">built right.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Architecture first,<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">code second.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 mb-4"><Icon /></div>
                  <div className="text-xs font-bold text-emerald-500 tracking-widest mb-2">{step.step}</div>
                  <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-emerald-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">The backend stack.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-emerald-300 text-sm font-medium hover:bg-emerald-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Built in, not bolted on.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-emerald-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">Need a backend<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">that scales?</span></h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">Book a free call. We'll review your architecture and give you an honest technical assessment.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-semibold shadow-2xl shadow-emerald-900/40 hover:shadow-emerald-900/60 hover:-translate-y-1 transition-all duration-300">
              Get a Free Consultation<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
